import React, { FC, useState } from 'react'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import Select from 'src/components/ui/select'
import { ISelectOption } from 'src/components/ui/select/SelectOption'
import { getPageContent } from 'src/helpers/pageContent'

const encodeFormData = (data: Record<string, string>) => {
  return Object.keys(data)
    .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
    .join('&')
}

interface IContactFormProps {
  className?: string
  options?: ISelectOption[]
}

const ContactForm: FC<IContactFormProps> = ({ className = '', options }) => {
  const { t, i18n } = useTranslation('contact-page')
  const cmsJson = getPageContent('/contact', i18n.language)

  const [userContact, setUserContact] = useState('')
  const [message, setMessage] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [isSend, setIsSend] = useState(false)
  const [isSendError, setIsSendError] = useState(false)
  const [selectedContactOption, selectContactOption] = useState<ISelectOption | null>(null)

  const contactOptions: Array<ISelectOption> =
    options ||
    cmsJson?.contactOptions?.map((opt: any) => ({
      optionId: opt.optionId,
      label: t(opt.label) || opt.label,
    })) || [
      {
        optionId: 'product-dev',
        label: t('Product Development'),
      },
      {
        optionId: 'sport',
        label: t('Sport'),
      },
      {
        optionId: 'imagination',
        label: t('Imagination & Evaluation'),
      },
      {
        optionId: 'product-design',
        label: t('Product Design'),
      },
      {
        optionId: 'growth',
        label: t('Growth & Innovation'),
      },
      {
        optionId: 'tdc',
        label: t('Technology-driven Consultancy'),
      },
    ]

  const [showErrors, setShowErrors] = useState(false)

  const isServiceValid = Boolean(selectedContactOption)
  const isContactValid = userContact.trim().length >= 3
  const isFormValid = isServiceValid && isContactValid

  const selectedServiceValue =
    typeof selectedContactOption?.label === 'string'
      ? selectedContactOption.label
      : selectedContactOption?.optionId || ''

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!isFormValid) {
      setShowErrors(true)
      return
    }

    if (isSending) return

    setIsSending(true)
    setIsSend(false)
    setIsSendError(false)

    const isDev =
      process.env.NODE_ENV === 'development' ||
      (typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))

    try {
      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encodeFormData({
          'form-name': 'contact',
          service: selectedServiceValue,
          contact: userContact,
          message: message,
        }),
      })

      if (res.ok || (isDev && res.status === 404)) {
        if (isDev && res.status === 404) {
          console.info('[Dev] Netlify Form submission simulated on localhost:', {
            service: selectedServiceValue,
            contact: userContact,
            message,
          })
        }
        setUserContact('')
        setMessage('')
        selectContactOption(null)
        setShowErrors(false)
        setIsSend(true)
      } else {
        console.error('Netlify Form submission failed:', res.status, res.statusText)
        setIsSendError(true)
      }
    } catch (err) {
      if (isDev) {
        console.info('[Dev] Netlify Form submission simulated on localhost (catch):', err)
        setUserContact('')
        setMessage('')
        selectContactOption(null)
        setShowErrors(false)
        setIsSend(true)
      } else {
        console.error('Netlify Form submission error:', err)
        setIsSendError(true)
      }
    } finally {
      setIsSending(false)
    }
  }

  const successMessage = () => {
    if (isSend) {
      return t('Message has been send successfully!')
    }

    return ''
  }

  const handleUserContactChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsSend(false)
    setIsSendError(false)
    setUserContact(e.target.value)
  }

  const handleSelectOption = (option: ISelectOption) => {
    setIsSend(false)
    setIsSendError(false)
    selectContactOption(option)
  }

  return (
    <form
      name="contact"
      action="/"
      method="POST"
      data-netlify="true"
      data-netlify-honeypot="bot-field"
      className={`w-full ${className}`}
      onSubmit={onSubmit}
      noValidate
    >
      <input type="hidden" name="form-name" value="contact" />
      <input type="hidden" name="service" value={selectedServiceValue} />
      <p className="hidden" aria-hidden="true">
        <label>
          Don’t fill this out if you're human: <input name="bot-field" tabIndex={-1} />
        </label>
      </p>
      <div className="w-full">
        <p className="text-secondaryGray800 dark:text-white text-left text-base md:text-lg font-semibold mb-6 md:mb-8 text-balance [text-wrap:balance] pt-0.5 md:pt-1">
          {t('Select interesting service')}
        </p>
        <div className="flex flex-col gap-6 md:gap-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <div>
              <p className="font-semibold text-sm mb-2 lowercase text-black dark:text-white/80">{t('service')}</p>
              <Select
                className="w-full"
                placeholder={t('Select subject...')}
                onSelect={handleSelectOption}
                selectedItem={selectedContactOption}
                options={contactOptions}
              />
              {showErrors && !isServiceValid && (
                <p className="text-red text-xs mt-1.5 font-medium lowercase">
                  {t('please select a service')}
                </p>
              )}
            </div>
            <div>
              <p className="font-semibold text-sm mb-2 lowercase text-black dark:text-white/80">
                {t('your contact')}
              </p>
              <input
                name="contact"
                style={{ outline: 'none', boxShadow: 'none' }}
                className={`w-full h-14 px-5 md:px-6 border bg-highlight-subtle-lowest backdrop-blur-lowest outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0 focus-visible:ring-0 shadow-none transition-colors duration-300 ease-out rounded-large text-black dark:text-white placeholder:text-secondaryGray600 dark:placeholder:text-slate-500 placeholder:lowercase ${
                  showErrors && !isContactValid
                    ? 'border-red'
                    : userContact
                    ? 'border-red'
                    : 'border-secondaryGray500 dark:border-white/20 hover:border-red focus:border-red'
                }`}
                placeholder={t('your phone number or e-mail address')}
                value={userContact}
                onChange={handleUserContactChange}
              />
              {showErrors && !isContactValid && (
                <p className="text-red text-xs mt-1.5 font-medium lowercase">
                  {t('please provide contact information')}
                </p>
              )}
            </div>
          </div>

          <div>
            <p className="font-semibold text-sm mb-2 lowercase text-black dark:text-white/80">
              {t('write message')}
            </p>
            <textarea
              name="message"
              rows={4}
              style={{ outline: 'none', boxShadow: 'none' }}
              className={`w-full min-h-[140px] p-5 md:p-6 border bg-highlight-subtle-lowest backdrop-blur-lowest outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0 focus-visible:ring-0 shadow-none transition-colors duration-300 ease-out text-black dark:text-white placeholder:text-secondaryGray600 dark:placeholder:text-slate-500 placeholder:lowercase resize-none rounded-container-base ${
                message
                  ? 'border-red'
                  : 'border-secondaryGray500 dark:border-white/20 hover:border-red focus:border-red'
              }`}
              placeholder={t('how can we help you')}
              value={message}
              onChange={(e) => {
                setIsSend(false)
                setIsSendError(false)
                setMessage(e.target.value)
              }}
            />
          </div>
        </div>
        <div className="mt-8 flex flex-col items-end w-full">
          {isSend && (
            <p className="mb-4 text-lightGreen font-semibold text-lg text-right h-8">
              {successMessage()}
            </p>
          )}
          {isSendError && (
            <p className="mb-4 text-red font-semibold text-sm md:text-base text-right">
              {t('Failed to send message. Please try again or contact us directly.')}
            </p>
          )}
          <button
            type="submit"
            disabled={isSending}
            className="group relative inline-flex items-center text-title-medium md:text-title-large font-palanquin font-semibold text-on-surface select-none cursor-pointer outline-none focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span
              className="absolute left-0 inline-flex items-center text-primary opacity-0 -translate-x-4 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-700 ease-[cubic-bezier(0.65,0,0.35,1)] shrink-0 pointer-events-none"
              aria-hidden="true"
            >
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5 md:w-6 md:h-6"
                aria-hidden="true"
              >
                <line x1="2" y1="8" x2="14" y2="8" />
                <polyline points="9 3 14 8 9 13" />
              </svg>
            </span>
            <span className="inline-block border-b border-dashed border-primary pb-0.5 transition-transform duration-700 ease-[cubic-bezier(0.65,0,0.35,1)] group-hover:translate-x-7 md:group-hover:translate-x-8">
              {isSending ? t('sending...') : t('submit message')}
            </span>
          </button>
        </div>
      </div>
    </form>
  )
}

export default ContactForm
